/**
 * Aplicaciones — v1.8. User-facing "Apps publicadas" screen.
 *
 * Lists every active deployment (across all providers) for the current
 * user, with real preview thumbnails served by the existing endpoint
 * GET /api/deployments/{id}/thumbnail (backed by headless Chromium).
 *
 * Filters: Todas / En línea / En desarrollo / Pausadas.
 * Actions: open live URL (external icon), open workspace card body.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, SlidersHorizontal, ExternalLink, MoreVertical, Eye, Coins, Activity, Rocket, Clock, Share2, RotateCcw } from 'lucide-react';
import { apiService } from '../services/api';
import { UserTopBar } from '../components/user/UserTopBar';
import { toast } from 'sonner';

type Deployment = {
  workspace_id: string;
  workspace_name: string;
  workspace_description?: string | null;
  url: string | null;
  status: string;
  environment: string;
  published_at: string;
  size_bytes: number;
  project_stack: string | null;
  tech_stack: string[];
  thumbnail_url: string | null;
};

type Filter = 'all' | 'online' | 'dev' | 'paused';

const FILTER_META: Record<Filter, { label: string; dot: string }> = {
  all: { label: 'Todas', dot: '' },
  online: { label: 'En línea', dot: 'hsl(150 65% 55%)' },
  dev: { label: 'En desarrollo', dot: 'hsl(38 90% 60%)' },
  paused: { label: 'Pausadas', dot: 'hsl(0 70% 60%)' },
};

const statusToFilter = (s: string): Filter => {
  const t = (s || '').toLowerCase();
  if (t.includes('running') || t.includes('active')) return 'online';
  if (t.includes('stop') || t.includes('paused')) return 'paused';
  return 'dev';
};

const statusLabel = (f: Filter) => {
  if (f === 'online') return 'En línea';
  if (f === 'dev') return 'En desarrollo';
  return 'Pausada';
};

const timeAgo = (iso: string) => {
  try {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return 'hace unos segundos';
    if (diff < 3600) return `hace ${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `hace ${Math.floor(diff / 3600)} h`;
    return `hace ${Math.floor(diff / 86400)} días`;
  } catch { return ''; }
};

export default function Aplicaciones() {
  const nav = useNavigate();
  const [me, setMe] = useState<any>(null);
  const [items, setItems] = useState<Deployment[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState<Filter>('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [m, d] = await Promise.all([
        apiService.getMe().catch(() => null),
        apiService.listDeployments(false).catch(() => []),
      ]);
      setMe(m);
      setItems(d as any);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => items.filter((it) => {
    const f = statusToFilter(it.status);
    if (filter !== 'all' && f !== filter) return false;
    if (q && !((it.workspace_name || '').toLowerCase().includes(q.toLowerCase())
             || (it.url || '').toLowerCase().includes(q.toLowerCase()))) return false;
    return true;
  }), [items, filter, q]);

  return (
    <div className="min-h-screen bg-slate-950 nx-circuit text-slate-100" data-testid="aplicaciones-page">
      <UserTopBar title="Aplicaciones" avatarUrl={me?.avatar_url} />

      <main className="max-w-4xl mx-auto px-4 py-8 space-y-5">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold">Aplicaciones publicadas</h1>
          <p className="text-sm mt-1" style={{ color: 'hsl(var(--text-muted))' }}>
            Administra todas tus aplicaciones disponibles para tus usuarios.
          </p>
        </div>

        {/* Search + filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div
            className="flex-1 flex items-center gap-2 rounded-xl px-3 py-2"
            style={{ background: 'hsl(var(--bg-elev) / 0.7)', border: '1px solid hsl(var(--border))' }}
          >
            <Search size={14} style={{ color: 'hsl(var(--text-muted))' }} />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por nombre o dominio…"
              className="flex-1 bg-transparent outline-none text-sm"
              style={{ color: 'hsl(var(--text))' }}
              data-testid="aplicaciones-search"
            />
          </div>
          <button
            className="w-10 h-10 rounded-xl grid place-items-center"
            style={{ background: 'hsl(var(--bg-elev) / 0.7)', border: '1px solid hsl(var(--border))', color: 'hsl(var(--text-muted))' }}
            aria-label="Filtros"
          >
            <SlidersHorizontal size={14} />
          </button>
        </div>

        <div className="flex flex-wrap gap-2">
          {(Object.keys(FILTER_META) as Filter[]).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`aplicaciones-filter-${f}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition"
              style={{
                background: filter === f ? 'hsl(var(--accent) / 0.16)' : 'hsl(var(--bg-elev) / 0.6)',
                border: `1px solid ${filter === f ? 'hsl(var(--accent) / 0.5)' : 'hsl(var(--border))'}`,
                color: filter === f ? 'hsl(var(--accent))' : 'hsl(var(--text-muted))',
              }}
            >
              {FILTER_META[f].dot && <span className="w-1.5 h-1.5 rounded-full" style={{ background: FILTER_META[f].dot }} />}
              {FILTER_META[f].label}
            </button>
          ))}
        </div>

        {/* Card list */}
        <div className="space-y-3">
          {loading && <div className="text-center py-10 text-slate-500 text-sm">Cargando aplicaciones…</div>}
          {!loading && filtered.length === 0 && (
            <div
              className="rounded-2xl p-10 text-center"
              style={{ background: 'hsl(var(--bg-elev) / 0.5)', border: '1px dashed hsl(var(--border))' }}
              data-testid="aplicaciones-empty"
            >
              <div className="text-sm font-medium">Aún no tienes aplicaciones publicadas</div>
              <div className="text-xs mt-1" style={{ color: 'hsl(var(--text-muted))' }}>
                Cuando publiques un proyecto aparecerá aquí con una miniatura real.
              </div>
              <button
                onClick={() => nav('/home')}
                className="mt-4 inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold"
                style={{ background: 'hsl(var(--accent))', color: 'hsl(210 40% 8%)' }}
                data-testid="aplicaciones-empty-cta"
              >
                Crear una aplicación
              </button>
            </div>
          )}
          {filtered.map((it) => {
            const f = statusToFilter(it.status);
            return (
              <div
                key={it.workspace_id}
                data-testid={`aplicacion-card-${it.workspace_id}`}
                className="rounded-2xl overflow-hidden flex flex-col sm:flex-row"
                style={{ background: 'hsl(var(--bg-elev) / 0.75)', border: '1px solid hsl(var(--border))', backdropFilter: 'blur(10px)' }}
              >
                <div
                  className="sm:w-40 h-32 sm:h-auto relative shrink-0 grid place-items-center"
                  style={{
                    background: 'linear-gradient(135deg, hsl(215 25% 8%), hsl(215 20% 12%))',
                  }}
                >
                  {it.thumbnail_url ? (
                    <img
                      src={it.thumbnail_url.startsWith('http') ? it.thumbnail_url : ((process.env.REACT_APP_BACKEND_URL || '') + it.thumbnail_url)}
                      alt={it.workspace_name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                    />
                  ) : (
                    <div className="text-xs" style={{ color: 'hsl(var(--text-muted))' }}>Sin captura</div>
                  )}
                </div>

                <div className="flex-1 p-4 flex flex-col justify-between gap-2">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="text-sm font-semibold">{it.workspace_name}</div>
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px]"
                        style={{
                          background: `${FILTER_META[f].dot} / 0.14`,
                          color: FILTER_META[f].dot,
                        }}
                      >
                        <span className="w-1.5 h-1.5 rounded-full" style={{ background: FILTER_META[f].dot }} />
                        {statusLabel(f)}
                      </span>
                    </div>
                    <p className="text-xs mt-1 line-clamp-2" style={{ color: 'hsl(var(--text-muted))' }}>
                      {it.workspace_description || '—'}
                    </p>
                    {it.url && (
                      <a
                        href={it.url} target="_blank" rel="noopener noreferrer"
                        className="mt-2 inline-flex items-center gap-1 text-xs font-mono"
                        style={{ color: 'hsl(var(--accent))' }}
                      >
                        {it.url.replace(/^https?:\/\//, '')}
                        <ExternalLink size={11} />
                      </a>
                    )}
                    <div className="mt-1 text-[11px] flex items-center gap-2 flex-wrap" style={{ color: 'hsl(var(--text-muted))' }}>
                      <span>Actualizada {timeAgo(it.published_at)}</span>
                      {typeof (it as any).credits_consumed === 'number' && (
                        <span
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md tabular-nums"
                          data-testid={`aplicacion-credits-${it.workspace_id}`}
                          title="Créditos consumidos por esta aplicación"
                          style={{
                            background: 'hsl(var(--accent) / 0.10)',
                            border: '1px solid hsl(var(--accent) / 0.28)',
                            color: 'hsl(var(--accent))',
                            fontSize: '10px',
                          }}
                        >
                          <Coins size={10} />
                          {Math.round((it as any).credits_consumed).toLocaleString('es-ES')}
                        </span>
                      )}
                    </div>
                    {/* Último Deploy · métricas de app: uptime + deploys + tiempo promedio */}
                    <div className="mt-2 grid grid-cols-3 gap-2" data-testid={`aplicacion-metrics-${it.workspace_id}`}>
                      <MetricChip
                        icon={<Activity size={10} />}
                        label="Estado"
                        value={(it as any).is_up ? 'En línea' : 'Detenida'}
                        tone={(it as any).is_up ? 'ok' : 'warn'}
                      />
                      <MetricChip
                        icon={<Rocket size={10} />}
                        label="Deploys"
                        value={((it as any).total_deploys ?? 0).toString()}
                      />
                      <MetricChip
                        icon={<Clock size={10} />}
                        label="Prom."
                        value={
                          typeof (it as any).avg_deploy_ms === 'number'
                            ? `${((it as any).avg_deploy_ms / 1000).toFixed(1)}s`
                            : '—'
                        }
                      />
                    </div>
                  </div>

                  <div className="flex items-center gap-2 justify-end">
                    <button
                      onClick={() => nav(`/w/${it.workspace_id}`)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium"
                      style={{ background: 'hsl(var(--bg))', color: 'hsl(var(--text))', border: '1px solid hsl(var(--border))' }}
                      data-testid={`aplicacion-open-workspace-${it.workspace_id}`}
                    >
                      <Eye size={12} /> Editar
                    </button>
                    {it.url && (
                      <>
                        <a
                          href={it.url} target="_blank" rel="noopener noreferrer"
                          className="w-8 h-8 rounded-full grid place-items-center"
                          style={{ background: 'hsl(var(--accent) / 0.14)', color: 'hsl(var(--accent))', border: '1px solid hsl(var(--accent) / 0.35)' }}
                          aria-label="Abrir aplicación"
                          data-testid={`aplicacion-open-live-${it.workspace_id}`}
                        >
                          <ExternalLink size={12} />
                        </a>
                        <button
                          onClick={async () => {
                            try {
                              if ((navigator as any).share) {
                                await (navigator as any).share({ title: it.workspace_name, url: it.url });
                              } else {
                                await navigator.clipboard.writeText(it.url as string);
                                toast.success('URL copiada al portapapeles');
                              }
                            } catch { /* user cancelled or unsupported */ }
                          }}
                          className="w-8 h-8 rounded-full grid place-items-center"
                          style={{ background: 'hsl(var(--bg) / 0.6)', color: 'hsl(var(--text))', border: '1px solid hsl(var(--border))' }}
                          aria-label="Compartir"
                          data-testid={`aplicacion-share-${it.workspace_id}`}
                          title="Compartir"
                        >
                          <Share2 size={12} />
                        </button>
                      </>
                    )}
                    {((it as any).rollback_candidates ?? 0) > 0 && (
                      <button
                        onClick={async () => {
                          if (!confirm(`¿Revertir "${it.workspace_name}" a la versión anterior?`)) return;
                          try {
                            const versions: any = await (apiService as any).listPublishedVersions?.(it.workspace_id);
                            const list: any[] = Array.isArray(versions?.versions) ? versions.versions : (Array.isArray(versions) ? versions : []);
                            const previous = list.find((v: any) => !v.active);
                            if (!previous) { toast.error('No hay versión anterior disponible.'); return; }
                            await (apiService as any).rollbackVersion?.(it.workspace_id, previous.version);
                            toast.success(`Rollback a v${previous.version} completado.`);
                            await load();
                          } catch (e: any) {
                            toast.error(e?.response?.data?.detail || 'No se pudo revertir.');
                          }
                        }}
                        className="w-8 h-8 rounded-full grid place-items-center"
                        style={{ background: 'hsl(38 90% 60% / 0.14)', color: 'hsl(38 90% 60%)', border: '1px solid hsl(38 90% 60% / 0.35)' }}
                        aria-label="Rollback"
                        data-testid={`aplicacion-rollback-${it.workspace_id}`}
                        title="Revertir a la versión anterior"
                      >
                        <RotateCcw size={12} />
                      </button>
                    )}
                    <button
                      className="w-8 h-8 rounded-full grid place-items-center"
                      style={{ background: 'hsl(var(--bg) / 0.5)', color: 'hsl(var(--text-muted))', border: '1px solid hsl(var(--border))' }}
                      aria-label="Más opciones"
                    >
                      <MoreVertical size={12} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </main>
    </div>
  );
}

const MetricChip: React.FC<{ icon: React.ReactNode; label: string; value: string; tone?: 'ok' | 'warn' }> = ({ icon, label, value, tone }) => {
  const color = tone === 'ok' ? 'hsl(150 65% 60%)' : tone === 'warn' ? 'hsl(38 90% 60%)' : 'hsl(var(--text-muted))';
  return (
    <div
      className="rounded-lg px-2 py-1 flex items-center gap-1.5 min-w-0"
      style={{ background: 'hsl(var(--bg-elev) / 0.55)', border: '1px solid hsl(var(--border))', color }}
    >
      {icon}
      <div className="flex flex-col min-w-0 leading-tight">
        <span className="text-[9px] uppercase tracking-widest opacity-70">{label}</span>
        <span className="text-[10px] font-semibold truncate">{value}</span>
      </div>
    </div>
  );
};
